import { Cross2Icon } from '@radix-ui/react-icons';
import { type JSX, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';

type DrawerSize = 'sm' | 'md' | 'lg';

const sizeClassName: Record<DrawerSize, string> = {
  sm: 'md:max-w-lg',
  md: 'md:max-w-xl',
  lg: 'md:max-w-3xl',
};

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  trigger?: ReactNode;
  headerContent?: ReactNode;
  footerContent?: ReactNode;
  children: ReactNode;
  size?: DrawerSize;
  showClose?: boolean;
  closeLabel?: string;
}

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  trigger,
  headerContent,
  footerContent,
  children,
  size = 'lg',
  showClose = true,
  closeLabel = 'Close',
}: DrawerProps): JSX.Element {
  const hasTrigger = trigger !== null && trigger !== undefined;
  const hasTitle = title !== null && title !== undefined;
  const hasDescription = description !== null && description !== undefined;
  const hasFooter = footerContent !== null && footerContent !== undefined;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {hasTrigger ? <Dialog.Trigger asChild>{trigger}</Dialog.Trigger> : null}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content
          className={clsx(
            'fixed inset-x-0 bottom-0 z-50 flex max-h-screen flex-col rounded-t-3xl border-t border-surface-subtle bg-white shadow-2xl focus:outline-none md:inset-y-0 md:left-auto md:right-0 md:w-full md:rounded-none md:border-l',
            sizeClassName[size],
          )}
        >
          <div className="flex items-center justify-between gap-4 border-b border-surface-subtle px-5 py-4">
            <div className="flex flex-col">
              {hasTitle ? (
                <Dialog.Title className="text-lg font-semibold text-text-primary">
                  {title}
                </Dialog.Title>
              ) : null}
              {hasDescription ? (
                <Dialog.Description className="text-sm text-text-primary/70">
                  {description}
                </Dialog.Description>
              ) : null}
              {headerContent}
            </div>
            {showClose ? (
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-surface-subtle text-text-primary/70 transition hover:border-brand-primary/50 hover:bg-brand-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 md:h-10 md:w-10"
                  aria-label={closeLabel}
                >
                  <Cross2Icon className="h-4 w-4" aria-hidden />
                </button>
              </Dialog.Close>
            ) : null}
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {hasFooter ? (
            <div className="border-t border-surface-subtle bg-white px-5 py-4">{footerContent}</div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
